import React from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";

const sanitizeHashtagInput = (rawValue) =>
  String(rawValue || "").replace(/[^#A-Za-z0-9ㄱ-ㅎㅏ-ㅣ가-힣_\s]/g, "");

function SortableHashtag({ id, onRemove, children }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
  };

  return (
    <span
      ref={setNodeRef}
      className="instagram-hashtag-token"
      style={style}
      {...attributes}
      {...listeners}
    >
      {children}
      <button
        type="button"
        className="instagram-hashtag-token-delete"
        onClick={onRemove}
        onPointerDown={(e) => e.stopPropagation()}
        aria-label={`${children} 삭제`}
        title="삭제"
      >
        ×
      </button>
    </span>
  );
}

export default function InstagramHashtagEditor({
  tags = [],
  inputValue = "",
  onInputChange,
  onAddInput,
  onRemoveTag,
  onReorderTags,
  favoriteTags = [],
  onAddFavoriteTag,
  onDeleteFavoriteTag,
  onOpenFavoriteTagModal,
  legacyRawValue = "",
  onClearLegacyRawValue,
  placeholder = "해시태그 입력",
  addButtonText = "추가",
  maxTags = 5,
}) {
  const inputRef = React.useRef(null);
  const composingRef = React.useRef(false);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const focusInput = () => {
    inputRef.current?.focus();
  };

  const submitInput = (value = inputValue) => {
    if (composingRef.current) return;
    onAddInput?.(value);
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = tags.indexOf(active.id);
    const newIndex = tags.indexOf(over.id);

    if (oldIndex < 0 || newIndex < 0) return;
    onReorderTags?.(arrayMove(tags, oldIndex, newIndex));
  };

  const handleInputChange = (e) => {
    const value = e.target.value;

    // Post_v3에서 사용하던 방식과 동일하게 처리한다.
    // 한글 조합 중 스페이스로 글자가 확정되면 끝 공백이 포함된 값이 onChange로 들어온다.
    if (value.includes(" ") && !value.trim().includes(" ")) {
      composingRef.current = false;
      onAddInput?.(value);
      return;
    }

    onInputChange?.(sanitizeHashtagInput(value));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitInput(e.currentTarget.value);
      return;
    }

    if (e.key === " " && !composingRef.current) {
      e.preventDefault();
      submitInput(e.currentTarget.value);
    }
  };

  if (legacyRawValue) {
    return (
      <div className="instagram-hashtag-editor">
        <style>{styles}</style>
        <div className="instagram-hashtag-legacy-box">
          <div className="instagram-hashtag-legacy-title">#태그 형식이 아닌 입력값</div>
          <div className="instagram-hashtag-legacy-guide">
            태그별로 수정하려면 아래 값을 삭제한 후, #이 포함된 해시태그로 다시 등록해 주세요.
          </div>
          <div className="instagram-hashtag-legacy-value">{legacyRawValue}</div>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={onClearLegacyRawValue}
          >
            삭제 후 다시 등록
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="instagram-hashtag-editor">
      <style>{styles}</style>

      <div className="instagram-hashtag-entry-row">
        <div className="instagram-hashtag-input-shell" onClick={focusInput}>
          {/* 리뉴얼 이전과 동일하게 입력창 안에서 태그를 렌더링한다. */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={tags} strategy={rectSortingStrategy}>
              {tags.map((tag, index) => (
                <SortableHashtag
                  key={tag}
                  id={tag}
                  onRemove={() => onRemoveTag?.(index)}
                >
                  {tag}
                </SortableHashtag>
              ))}
            </SortableContext>
          </DndContext>

          <input
            ref={inputRef}
            type="text"
            className="instagram-hashtag-input"
            aria-label="해시태그 입력"
            placeholder={placeholder}
            value={inputValue || ""}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onCompositionStart={() => {
              composingRef.current = true;
            }}
            onCompositionEnd={() => {
              composingRef.current = false;
            }}
          />
          {/* 참고 HTML의 입력창 오른쪽 Enter 아이콘: 실제 추가는 기존 Enter / 추가 버튼으로 처리 */}
          <svg
            className="instagram-hashtag-input-icon"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <polyline points="9 10 4 15 9 20" />
            <path d="M20 4v7a4 4 0 0 1-4 4H4" />
          </svg>
        </div>

        {/* 기존 '추가' 버튼의 동작/위치는 유지하고 높이만 입력창과 맞춤 */}
        <button
          type="button"
          className="btn btn-outline instagram-hashtag-add-button"
          onClick={() => submitInput(inputValue)}
        >
          {addButtonText}
        </button>
      </div>

      {/* 태그 수 표시는 리뉴얼 디자인 그대로 입력 영역 하단 우측에 유지한다. */}
      <div className="instagram-hashtag-tag-list">
        <span className={`instagram-hashtag-count ${tags.length > maxTags ? "over" : ""}`}>
          {tags.length}/{maxTags}
        </span>
      </div>

      <div className="instagram-hashtag-favorite-row">
        <button
          type="button"
          className="instagram-hashtag-favorite-add-button"
          onClick={onOpenFavoriteTagModal}
          aria-label="즐겨찾는 태그 추가"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2Z" />
          </svg>
          <span>즐겨찾는 태그 추가</span>
        </button>

        <span className="instagram-hashtag-favorite-hint">
          즐겨찾기 태그를 클릭하면 바로 추가됩니다.
        </span>

        {(favoriteTags || []).map((tag, index) => (
          <button
            type="button"
            className="phrase-chip"
            key={tag.favorite_tag_seq || `${tag.tag_name}-${index}`}
            onClick={() => onAddFavoriteTag?.(tag.tag_name)}
          >
            {tag.tag_name}
            <span
              className="chip-delete"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteFavoriteTag?.(index);
              }}
            >
              ×
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

const styles = `
  .instagram-hashtag-editor {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  /* AI 자동발행 설정 HTML에서 'AI 자동 생성' 토글 오른쪽 입력 영역을 참고 */
  .instagram-hashtag-entry-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
  }

  .instagram-hashtag-input-shell {
    position: relative;
    min-width: 0;
    min-height: 38px;
    height: auto;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    align-content: center;
    flex-wrap: wrap;
    gap: 6px;
    padding: 3px 36px 3px 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: #fff;
    cursor: text;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .instagram-hashtag-input-shell:focus-within {
    border-color: #00e2e6;
    box-shadow: 0 0 0 3px rgba(0, 226, 230, 0.1);
  }

  .instagram-hashtag-input {
    display: block;
    width: auto !important;
    min-width: 100px !important;
    flex: 1 1 120px;
    height: 30px !important;
    box-sizing: border-box !important;
    padding: 0 2px !important;
    border: none !important;
    border-radius: 0 !important;
    outline: none !important;
    box-shadow: none !important;
    background: transparent !important;
    color: #111827 !important;
    font-family: 'Noto Sans KR', sans-serif !important;
    font-size: 13px !important;
    font-weight: 400 !important;
  }

  .instagram-hashtag-input::placeholder {
    color: #9ca3af;
  }

  .instagram-hashtag-input-icon {
    position: absolute;
    top: 18px;
    right: 12px;
    transform: translateY(-50%);
    color: #9ca3af;
    pointer-events: none;
  }

  /* 기존 추가 버튼은 그대로 사용하고, 입력창과 높이만 일치시킨다. */
  .instagram-hashtag-editor .instagram-hashtag-add-button {
    min-width: 68px;
    height: 38px !important;
    min-height: 38px !important;
    box-sizing: border-box;
    padding: 0 15px !important;
    align-self: center;
  }

  .instagram-hashtag-editor .instagram-hashtag-add-button:hover {
    border-color: var(--primary-mint, #00e2e6) !important;
    color: var(--deep-navy, #111827);
    background: #f0fdfe !important;
  }

  /* 하단에는 기존 태그 개수만 표시한다. 태그 자체는 입력창 내부에 있다. */
  .instagram-hashtag-tag-list {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px 16px;
    min-width: 0;
    min-height: 20px;
  }

  /* 리뉴얼 이전의 타원형 토큰 스타일만 복원한다. */
  .instagram-hashtag-token {
    min-height: 30px;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0 10px;
    border: 1px solid #EEEEEE;
    border-radius: 999px;
    background: #F7F7F7;
    color: #374151;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    cursor: grab;
    touch-action: none;
  }

  .instagram-hashtag-token-delete {
    width: 16px;
    height: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: transparent;
    color: #9ca3af;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    transition: 0.2s;
  }

  .instagram-hashtag-token-delete:hover {
    color: #dc2626;
    transform: scale(1.2);
  }

  /* 원래 제공하던 개수 안내는 목록 오른쪽에 보조정보로 유지한다. */
  .instagram-hashtag-count {
    margin-left: auto;
    font-size: 12px;
    color: #8e8e8e;
    white-space: nowrap;
  }

  .instagram-hashtag-count.over {
    color: #dc2626;
  }

  /* 기존 즐겨찾기 추가 버튼의 스타일만 변경한다. 위치와 클릭 기능은 기존과 동일. */
  .instagram-hashtag-favorite-add-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    min-height: 34px;
    padding: 7px 12px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: #fff;
    color: #475569;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.4;
    white-space: nowrap;
    cursor: pointer;
    transition: border-color 0.2s, background-color 0.2s, color 0.2s;
  }

  .instagram-hashtag-favorite-add-button svg {
    flex-shrink: 0;
  }

  .instagram-hashtag-favorite-add-button:hover {
    border-color: var(--primary-mint, #00e2e6);
    background: #f0fdfe;
    color: #0a1a2f;
  }

  .instagram-hashtag-favorite-add-button:focus-visible {
    outline: 2px solid var(--primary-mint, #00e2e6);
    outline-offset: 2px;
  }

  .instagram-hashtag-favorite-row {
    min-height: 38px;
    display: none !important; /* 즐겨찾기 버튼·안내·태그 목록 전체 숨김 */
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 10px;
  }

  .instagram-hashtag-favorite-hint {
    flex: 0 0 auto;
    color: #8e8e8e;
    font-size: 12px;
    white-space: nowrap;
  }

  .instagram-hashtag-editor .phrase-chip {
    padding: 8px 16px;
    border-radius: 20px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    background: var(--bg-color, #f9fafb);
    border: 1px solid #EEEEEE;
    color: #374151;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: 0.2s;
  }

  .instagram-hashtag-editor .phrase-chip:not(.chip-new):hover {
    border: 1px solid var(--primary-mint) !important;
    color: #00736D;
    background: #f0fdfe !important;
  }

  .instagram-hashtag-editor .phrase-chip:hover {
    background: #e2e8f0;
    border-color: #cbd5e1;
  }

  .instagram-hashtag-editor .chip-delete {
    font-size: 14px;
    color: #9ca3af;
    padding-left: 2px;
    transition: 0.2s;
  }

  .instagram-hashtag-editor .chip-delete:hover {
    color: #dc2626;
    transform: scale(1.2);
  }

  .instagram-hashtag-legacy-box {
    border: 1px solid #f59e0b;
    border-radius: 10px;
    background: #fffbeb;
    padding: 16px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .instagram-hashtag-legacy-title {
    font-size: 13px;
    font-weight: 800;
    color: #92400e;
  }

  .instagram-hashtag-legacy-guide {
    font-size: 13px;
    color: #92400e;
    line-height: 1.5;
    word-break: keep-all;
  }

  .instagram-hashtag-legacy-value {
    width: 100%;
    padding: 12px 14px;
    border: 1px solid #fde68a;
    border-radius: 8px;
    background: #ffffff;
    color: #374151;
    font-size: 14px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
  }

  @media (max-width: 800px) {
    .instagram-hashtag-entry-row {
      grid-template-columns: 1fr;
    }

    .instagram-hashtag-add-button {
      width: 100%;
    }

    .instagram-hashtag-favorite-hint {
      width: 100%;
      white-space: normal;
    }
  }
`;
